import { useEffect } from 'react'
import { useApolloClient } from '@apollo/client'
import { useRenderJobsStore, type RenderJobStatus } from '@/stores/renderJobs'
import { GET_DIRECTOR_JOB } from '@/graphql/editTemplate'

const POLL_INTERVAL_MS = 4000
const TERMINAL_STATUSES: RenderJobStatus[] = ['completed', 'failed', 'cancelled']

// Mounted once in Layout.tsx (not per-page) so render jobs keep progressing in the background
// while the admin navigates elsewhere — polls every in-flight job in the store on one shared
// interval rather than one useQuery per job, since jobs are added/removed dynamically.
export function useRenderJobPolling() {
  const apolloClient = useApolloClient()
  const jobs = useRenderJobsStore((s) => s.jobs)
  const updateJob = useRenderJobsStore((s) => s.updateJob)

  useEffect(() => {
    const pendingJobs = jobs.filter((j) => !TERMINAL_STATUSES.includes(j.status))
    if (pendingJobs.length === 0) return

    const interval = setInterval(async () => {
      for (const job of pendingJobs) {
        try {
          const { data } = await apolloClient.query({
            query: GET_DIRECTOR_JOB,
            variables: { id: job.id },
            fetchPolicy: 'network-only',
          })
          const result = data?.getDirectorJob
          if (result) {
            updateJob(job.id, {
              status: result.status,
              percent: result.percent ?? job.percent,
              stage: result.stage ?? null,
              error: result.error ?? null,
              ...(result.result?.downloadUrl ? { downloadUrl: result.result.downloadUrl } : {}),
            })
          }
        } catch {
          // Transient poll failure — leave the job's last-known state as-is, try again next tick.
        }
      }
    }, POLL_INTERVAL_MS)

    return () => clearInterval(interval)
  }, [jobs, apolloClient, updateJob])
}
